// client/src/routes/app/properties/$propertyId/route.tsx — SCR-PROP-003, the tabs layout ·
// PROP-004 (04 §8.3, §5.10).
//
// §8.3's tab set, with the tabs that belong to later milestones simply absent — "tabs appear as
// their milestone lands". In M1 that was General, Coproprietari, Unități and Investiție & credit;
// „Costuri & P&L economic" landed with EXP-005 phase 2 (the M2 leftover, D-760–D-763) in the
// second position §8.3 gives it; „Asigurări" and „Documente" come with M3. A disabled tab
// advertising a screen that does not exist would be worse than no tab: it is a promise the app
// cannot keep and a support question it invites.
//
// Route-linked `<Link>`s (§5.10), so the active tab survives refresh and share, and TanStack
// puts `aria-current="page"` on it. The header carries the property's identity and the two
// destructive-ish actions, so every tab has them without repeating them four times. Identity
// is read off the detail's `general` block — the contract's shape (PROP-001).
//
// The head is the shared `DetailHeader` since the console's third visual pass (D-1842): the way
// back to the list, a building glyph, the address as the identity line and the chips under it.
// The delete confirm is the shared `ConfirmDialog`; the refusal a property with an ACTIVE
// contract earns still renders inside it, as before.
//
// EVERYTHING ABOUT THE APARTMENT, ON ITS PAGE (D-2840). The Product Owner asked that the profile
// show everything the application holds about the property. Five tabs joined the seven, each the
// module's own list with the property pinned: „Contracte și chiriași" (every contract, so the
// tenants who left are here too), „Chirii și încasări" (the rent history and the payments),
// „Contoare", „Echipamente" and „Reparații". Each is shown only to a membership holding the
// permission its route demands, the rule the „Documente" tab already follows. The order reads
// from who lives there, through money, to what is in the flat, to the paperwork and ownership.
// Twelve tabs do not fit one row on a laptop. They used to wrap onto a second row; since D-2900
// the shared `ProfileTabs` bar folds the ones that do not fit into a „Mai mult" menu instead,
// groups them, gives each its module's icon and stays in view while the page scrolls.

import { Link, Outlet, createFileRoute, linkOptions, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { DetailHeader } from '@/components/layout/DetailHeader';
import { ProfileTabs } from '@/components/layout/ProfileTabs';
import { RequirePermission } from '@/components/layout/route-guards';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { usePropertyErrorMessage } from '@/features/properties/errors';
import {
  AdministrationNotice,
  usePropertyWrite,
} from '@/features/properties/administration';
import { HandOverPropertyDialog } from '@/features/properties/HandOverPropertyDialog';
import { OccupancyChip } from '@/features/properties/OccupancyChip';
import { useDeleteProperty, useProperty } from '@/features/properties/queries';
import { useCan } from '@/session';
import { toast } from '@/stores/toast-store';

export const Route = createFileRoute('/app/properties/$propertyId')({
  component: PropertyDetailLayout,
});

function PropertyDetailLayout() {
  const { propertyId } = Route.useParams();
  const { t } = useTranslation();
  const property = useProperty(propertyId);
  // The tab lists the vault, which `Documents.Read` gates; a membership without it (none of the
  // seeded roles, today) sees one tab fewer rather than a tab that answers 403.
  const canReadDocuments = useCan('Documents.Read');
  const canReadLeasing = useCan('Leasing.Read');
  const canReadOperations = useCan('Operations.Read');

  if (property.isPending) return <LoadingState rows={4} />;
  if (property.isError) {
    return <ErrorState error={property.error} onRetry={() => void property.refetch()} />;
  }

  const { general } = property.data;

  return (
    <RequirePermission permission="Portfolio.Read">
      <DetailHeader
        backTo="/app/properties"
        backLabel={t('properties.list.title')}
        glyph="building"
        title={general.name}
        subtitle={`${general.addressStreet}, ${general.addressCity}`}
        chips={
          <>
            <OccupancyChip status={property.data.occupancy} />
            <span className="chip chip-neutral">
              {t(`enums.PropertyType.${general.propertyType}`)}
            </span>
            {/* Who runs the apartment, beside what it is and whether it is let — the three facts
                every tab needs in view. Absent rather than „—" when nobody does: a co-owned
                property mid-edit legitimately has no administrator, and a placeholder would read
                as a missing value rather than as an unmade decision (D-1753, FOUNDATION_BRIEF §2). */}
            {property.data.administratorDisplayName != null ? (
              <span className="chip chip-neutral">
                {t('properties.detail.administratedBy', {
                  name: property.data.administratorDisplayName,
                })}
              </span>
            ) : null}
            {general.buildingRef != null ? (
              <span className="chip chip-neutral">{general.buildingRef}</span>
            ) : null}
          </>
        }
        actions={
          <HeaderActions
            propertyId={propertyId}
            propertyName={general.name}
            transferredOutAt={property.data.transferredOutAt ?? null}
          />
        }
      />

      {/* D-2900: the shared section bar, in four groups — the apartment itself; who lives there
          and the money; what is in the flat; the paperwork and the ownership. The order is
          D-2840's and does not change; the groups only put a rule where the subject turns. */}
      <ProfileTabs
        label={t('properties.detail.tabsLabel')}
        groups={[
          [
            {
              label: t('properties.detail.tabs.general'),
              icon: 'information-circle',
              primary: true,
              link: linkOptions({ to: '/app/properties/$propertyId', params: { propertyId } }),
              exact: true,
            },
          ],
          [
            canReadLeasing && {
              label: t('properties.detail.tabs.contracts'),
              icon: 'document-text',
              primary: true,
              link: linkOptions({
                to: '/app/properties/$propertyId/contracts',
                params: { propertyId },
              }),
              ignoreSearch: true,
            },
            // Opens on the whole history (D-2841), and stays lit on the payments view and on the
            // single-month view alike — hence the search is left out of the active match.
            canReadLeasing && {
              label: t('properties.detail.tabs.rent'),
              icon: 'banknotes',
              primary: true,
              link: linkOptions({
                to: '/app/properties/$propertyId/rent',
                params: { propertyId },
                search: { all: true },
              }),
              ignoreSearch: true,
            },
            {
              label: t('properties.detail.tabs.costs'),
              icon: 'receipt',
              primary: true,
              link: linkOptions({ to: '/app/properties/$propertyId/costs', params: { propertyId } }),
              ignoreSearch: true,
            },
          ],
          [
            canReadOperations && {
              label: t('properties.detail.tabs.utilities'),
              icon: 'bolt',
              link: linkOptions({
                to: '/app/properties/$propertyId/utilities',
                params: { propertyId },
              }),
              ignoreSearch: true,
            },
            canReadOperations && {
              label: t('properties.detail.tabs.equipment'),
              icon: 'wrench',
              link: linkOptions({
                to: '/app/properties/$propertyId/equipment',
                params: { propertyId },
              }),
              ignoreSearch: true,
            },
            canReadOperations && {
              label: t('properties.detail.tabs.repairs'),
              icon: 'exclamation-triangle',
              primary: true,
              link: linkOptions({
                to: '/app/properties/$propertyId/repairs',
                params: { propertyId },
              }),
              ignoreSearch: true,
            },
            // „Asigurări" is §8.3's M3 tab, arriving with MAINT-006's slot; „Documente" is the
            // other M3 one, which DOC-004 left unbuilt and „Documentele mele" fills (D-2575). The
            // rule this file states above still holds — a tab appears when its screen does.
            {
              label: t('properties.detail.tabs.insurance'),
              icon: 'shield-check',
              link: linkOptions({
                to: '/app/properties/$propertyId/insurance',
                params: { propertyId },
              }),
            },
          ],
          [
            canReadDocuments && {
              label: t('properties.detail.tabs.documents'),
              icon: 'folder',
              link: linkOptions({
                to: '/app/properties/$propertyId/documents',
                params: { propertyId },
              }),
            },
            {
              label: t('properties.detail.tabs.coOwners'),
              icon: 'users',
              link: linkOptions({
                to: '/app/properties/$propertyId/co-owners',
                params: { propertyId },
              }),
            },
            {
              label: t('properties.detail.tabs.units'),
              icon: 'cube',
              link: linkOptions({ to: '/app/properties/$propertyId/units', params: { propertyId } }),
            },
            {
              label: t('properties.detail.tabs.investment'),
              icon: 'building-library',
              link: linkOptions({
                to: '/app/properties/$propertyId/investment',
                params: { propertyId },
              }),
            },
          ],
        ]}
      />

      <Outlet />
    </RequirePermission>
  );
}

/**
 * „Predă proprietatea" sits here rather than on a tab of its own (D-1901). It is a whole-property
 * act, like deleting one, and this row already owns a confirm-dialog pattern. A tab would give a
 * once-in-a-property's-life operation permanent room beside the four a landlord uses weekly; the
 * pending offer it produces goes on „General" instead, where the rest of the property's state is.
 *
 * It gates on `Portfolio.Write` AND `Org.ManageMembers`, which is not this row's usual test:
 * `POST …/handovers` demands both, as `POST …/co-owner-invitations` does and for the same
 * reason — giving the asset away is not a hired manager's act. Gating on `Portfolio.Write`
 * alone showed a `manager` a button whose only possible answer was a 403 (D-1905).
 */
function HeaderActions({
  propertyId,
  propertyName,
  transferredOutAt,
}: {
  propertyId: string;
  propertyName: string;
  transferredOutAt: string | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canWrite, blockedByAdministration, administratorName } = usePropertyWrite(
    propertyId,
    'Portfolio.Write',
  );
  const canHandOver = useCan('Org.ManageMembers');
  const remove = useDeleteProperty();
  const describe = usePropertyErrorMessage();
  const [confirming, setConfirming] = useState(false);
  const [handingOver, setHandingOver] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // BR-COOWN-09 rule 5 (D-1992). „Editează", „Predă proprietatea" and „Șterge" are
  // all operational writes on this apartment, so a co-owner who does not administer it gets
  // the sentence in their place rather than three buttons the flush would refuse.
  if (blockedByAdministration) {
    return <AdministrationNotice name={administratorName} />;
  }

  if (!canWrite) return null;

  const destroy = async () => {
    setFailure(null);
    try {
      await remove.mutateAsync({ path: { id: propertyId } });
      setConfirming(false);
      toast.success(t('properties.detail.deleted'));
      await navigate({ to: '/app/properties' });
    } catch (error) {
      // 05 PROP-001 criterion 2: a property with an ACTIVE contract is refused, with a
      // business-rule 422. That refusal belongs in the dialog that asked, not in a toast.
      setFailure(describe(error));
    }
  };

  return (
    <>
      <Link
        to="/app/properties/$propertyId/edit"
        params={{ propertyId }}
        className="btn btn-outline btn-sm"
      >
        <Icon name="cog" className="h-4 w-4" />
        {t('properties.detail.edit')}
      </Link>
      {/* Absent once the property has gone. Offering a second hand-over of an apartment that
          already left would be refused by the API (`handover-not-open` on the source), and a
          button whose only outcome is a refusal is worse than no button (D-1903). */}
      {canHandOver && transferredOutAt === null ? (
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => setHandingOver(true)}
        >
          <Icon name="arrow-top-right-on-square" className="h-4 w-4" />
          {t('properties.handover.offer.action')}
        </button>
      ) : null}

      <button
        type="button"
        className="btn btn-outline btn-error btn-sm"
        onClick={() => setConfirming(true)}
      >
        {t('common.delete')}
      </button>

      {/* Mounted only while it is open, unlike the confirm above it. `Modal` renders its
          `<dialog>` either way, and this one lives in the LAYOUT — so a closed copy would put
          „Predă proprietatea altei organizații" into the DOM of all six tabs, where it is text
          nobody asked for and a screen reader may still reach. */}
      {handingOver ? (
        <HandOverPropertyDialog
          open
          onClose={() => setHandingOver(false)}
          propertyId={propertyId}
          propertyName={propertyName}
        />
      ) : null}

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => void destroy()}
        title={t('properties.detail.deleteTitle')}
        description={t('properties.detail.deleteBody', { name: propertyName })}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        pendingLabel={t('common.saving')}
      >
        {failure ? (
          <div className="alert alert-error text-sm" role="alert">
            {failure}
          </div>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
